import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Link } from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { Routes } from '../../../../enums/Routes';
import { fullDate } from '../../hmsFormat';
import { SafetyRepSetting } from './SafetyRepSetting';
import classes from './Overview.module.css';

/** The risk matrix and the template every new survey uses */
export const SetupCard = ({
  setup,
}: {
  setup: HrWorkEnvironmentOverview['setup'];
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const { riskMatrix, template } = setup;
  const facts = [
    {
      key: 'riskMatrix',
      value: riskMatrix
        ? t(
            riskMatrix.bySafetyRep && setup.usesSafetyReps
              ? 'hrOverview.setup.riskMatrixBySafetyRep'
              : 'hrOverview.setup.riskMatrixByHr',
            {
              version: riskMatrix.version,
              date: riskMatrix.approvedAt
                ? fullDate(riskMatrix.approvedAt, i18n.language)
                : '',
            },
          )
        : t('hrOverview.setup.notSet'),
    },
    {
      key: 'template',
      value: template
        ? t('hrOverview.setup.templateValue', {
            version: template.version,
            count: template.factors,
          })
        : t('hrOverview.setup.notSet'),
    },
    {
      key: 'interval',
      value: t('hrOverview.setup.intervalValue', {
        months: setup.intervalMonths,
      }),
    },
    {
      key: 'minResponses',
      value: t('hrOverview.setup.minResponsesValue', {
        count: setup.minResponses,
      }),
    },
  ];
  return (
    <section
      aria-labelledby="setup-heading"
      className={`${classes.card} ${classes.tinted}`}
    >
      <Heading level={2} data-size="xs" id="setup-heading">
        {t('hrOverview.setup.title')}
      </Heading>
      <dl className={classes.facts}>
        {facts.map((fact) => (
          <div key={fact.key}>
            <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
              {t(`hrOverview.setup.${fact.key}`)}
            </dt>
            <dd className="ds-label" data-size="sm">
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
      <SafetyRepSetting setup={setup} />
      <div className={classes.links}>
        <Link asChild data-size="sm">
          <RouterLink to={Routes.HRRiskMatrixFullPath}>
            {t('hrOverview.setup.riskMatrix')}
          </RouterLink>
        </Link>
        <Link asChild data-size="sm">
          <RouterLink to={Routes.HRRiskFactorsFullPath}>
            {t('hrOverview.setup.template')}
          </RouterLink>
        </Link>
      </div>
    </section>
  );
};
